import React, { useState, useEffect, useCallback, useRef } from "react";
import { Modal, Spinner, ProgressBar } from "react-bootstrap";
import ReactCrop, { centerCrop, makeAspectCrop } from "react-image-crop";
import "react-image-crop/dist/ReactCrop.css";
import {
  FiImage, FiX, FiCamera, FiLink, FiAlertTriangle, FiInfo, FiSearch,
  FiToggleLeft, FiToggleRight, FiExternalLink, FiCheck,
} from "react-icons/fi";
import { uploadImageToCloudinary as sharedUpload } from "../../utilities/imageUpload";
import { searchLinkOptions, requestArtworkUploadIntent } from "../../actions/advertsActions.js";
import { MARKETS, SUPPORTED_COUNTRIES, DEFAULT_COUNTRY } from "../../config/markets.js";
import { getActiveCountry } from "../../utilities/activeCountry.js";
import AdSimulation from "./AdSimulation.js";
import {
  C, SLOT_META, LINK_KIND_LABEL, DESKTOP_ARTWORK_KEY,
  deriveDeviceType, splitAdArtwork,
} from "./adCenterMeta.js";
import AppSelect from "../../components/ui/AppSelect";

const MAX_FILE_BYTES = 10 * 1024 * 1024; // 10 MB

// ── Dates in the market's own day ────────────────────────────────────────────
// The form shows calendar dates. A banner that "starts 30 Aug" should start
// at 00:00 in Kampala (or Lusaka), and one that "ends 30 Aug" should run
// through the END of that day — not stop at 03:00 because the date was
// parsed as UTC midnight.
const marketTimeZone = (country) => MARKETS[country]?.timezone || "Africa/Kampala";

const offsetFor = (timeZone, date = new Date()) => {
  try {
    const parts = new Intl.DateTimeFormat("en-US", { timeZone, timeZoneName: "longOffset" })
      .formatToParts(date)
      .find((p) => p.type === "timeZoneName")?.value || "";
    const m = parts.match(/GMT([+-]\d{2}:\d{2})/);
    if (m) return m[1];
  } catch (e) { /* older browsers: fall through */ }
  return timeZone === "Africa/Lusaka" ? "+02:00" : "+03:00";
};

const toMarketDateInput = (value, timeZone) => {
  if (!value) return "";
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) return "";
  try {
    return new Intl.DateTimeFormat("en-CA", { timeZone, year: "numeric", month: "2-digit", day: "2-digit" }).format(d);
  } catch (e) {
    return d.toISOString().slice(0, 10);
  }
};

const startOfMarketDay = (dateInput, timeZone) =>
  dateInput ? `${dateInput}T00:00:00${offsetFor(timeZone, new Date(`${dateInput}T12:00:00Z`))}` : null;
const endOfMarketDay = (dateInput, timeZone) =>
  dateInput ? `${dateInput}T23:59:59${offsetFor(timeZone, new Date(`${dateInput}T12:00:00Z`))}` : null;

// Link types a marketing person can pick for a NEW banner. `deal` and
// `internal_screen` have no working destination on any client, so they are
// only shown when editing a legacy row that already carries them.
const LINK_CHOICES = [
  { value: "none",     label: "Nowhere (the banner is just a picture)" },
  { value: "product",  label: "A product" },
  { value: "vendor",   label: "A shop, restaurant or pharmacy" },
  { value: "category", label: "A shop category" },
  { value: "external", label: "A page on the site, or any web link" },
];

const SURFACE_CHIPS = [
  { key: "android", label: "Android app", needs: "phone" },
  { key: "ios",     label: "iOS app",     needs: "phone" },
  { key: "mweb",    label: "Mobile web",  needs: "phone" },
  { key: "desktop", label: "Desktop web", needs: "desktop" },
];

const labelStyle = {
  fontSize: 11, fontWeight: 700, color: C.textLight,
  textTransform: "uppercase", letterSpacing: "0.07em",
  display: "block", marginBottom: 6,
};
const fieldStyle = {
  background: C.inputBg, border: `1px solid ${C.border}`,
  borderRadius: C.radiusSm, padding: "9px 12px",
  fontSize: 13, color: C.text, width: "100%", outline: "none",
};
const sectionStyle = { marginBottom: 18 };
const hintStyle = { fontSize: 11, color: C.textLight, marginTop: 5, display: "block", lineHeight: 1.5 };

const InfoBox = ({ tone = "blue", children }) => {
  const palette = tone === "orange"
    ? { bg: C.orangeSoft, border: "#f3d9b4", icon: C.orange }
    : tone === "red"
      ? { bg: C.redSoft, border: "#f5c0cc", icon: C.red }
      : { bg: C.blueSoft, border: "#cfe0fb", icon: C.blue };
  const Icon = tone === "blue" ? FiInfo : FiAlertTriangle;
  return (
    <div style={{
      display: "flex", gap: 8, alignItems: "flex-start",
      background: palette.bg, border: `1px solid ${palette.border}`,
      borderRadius: C.radiusSm, padding: "9px 12px", marginTop: 7,
    }}>
      <Icon size={13} style={{ color: palette.icon, flexShrink: 0, marginTop: 1 }} />
      <span style={{ fontSize: 12, lineHeight: 1.5, color: C.textMid }}>{children}</span>
    </div>
  );
};

const smallButton = (bg, color = "#fff") => ({
  background: bg, color, border: "none", borderRadius: C.radiusSm,
  padding: "5px 10px", fontSize: 12, fontWeight: 600, cursor: "pointer",
  display: "inline-flex", alignItems: "center", gap: 5,
});

// ── One artwork box ───────────────────────────────────────────────────────────
// Drawn at the artwork's real proportion so nobody has to know what 16:9 or
// 6.34:1 means: the box IS the shape.
const ArtworkBox = ({ artwork, url, onPick, onRemove, maxWidth }) => {
  const inputRef = useRef(null);
  return (
    <div style={{ maxWidth: maxWidth || "100%" }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "baseline", gap: 8, marginBottom: 6 }}>
        <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>{artwork.label}</span>
        <span style={{ fontSize: 11, color: C.textLight }}>{artwork.displayLabel}</span>
      </div>
      {url ? (
        <div style={{
          position: "relative", width: "100%", aspectRatio: `${artwork.aspectRatio}`,
          border: `1px solid ${C.border}`, borderRadius: C.radius, overflow: "hidden", background: "#f0f2f5",
        }}>
          <img src={url} alt={artwork.label} style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", display: "block" }} />
          <div style={{ position: "absolute", top: 8, right: 8, display: "flex", gap: 6 }}>
            <button type="button" style={smallButton(C.blue)} onClick={() => inputRef.current?.click()}>
              <FiCamera size={12} /> Replace
            </button>
            <button type="button" style={smallButton(C.red)} onClick={onRemove}>
              <FiX size={12} /> Remove
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => inputRef.current?.click()}
          style={{
            width: "100%", aspectRatio: `${artwork.aspectRatio}`, minHeight: 64,
            display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 4,
            border: `2px dashed ${C.border}`, borderRadius: C.radius, background: C.inputBg, cursor: "pointer",
            color: C.text, padding: "8px 12px",
          }}
          onMouseEnter={(e) => (e.currentTarget.style.borderColor = C.blue)}
          onMouseLeave={(e) => (e.currentTarget.style.borderColor = C.border)}
        >
          <span style={{ display: "inline-flex", alignItems: "center", gap: 6, fontSize: 13, fontWeight: 600 }}>
            <FiCamera size={16} style={{ color: C.textLight }} /> Upload {artwork.label.toLowerCase()}
          </span>
          <span style={{ fontSize: 11, color: C.textLight }}>JPG or PNG, up to 10 MB</span>
        </button>
      )}
      <span style={hintStyle}>Shows on {artwork.surfaces}. Best at {artwork.canonWidthPx} by {artwork.canonHeightPx} pixels; the crop tool trims anything else to that shape.</span>
      <input
        ref={inputRef}
        type="file"
        hidden
        accept=".jpg,.jpeg,.png"
        onChange={(e) => { onPick(e.target.files?.[0]); e.target.value = ""; }}
      />
    </div>
  );
};

// ── Product / shop picker ─────────────────────────────────────────────────────
const LinkPicker = ({ linkType, linkTarget, linkParams, onPick, onClear }) => {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [error, setError] = useState("");
  const wantProducts = linkType === "product";

  useEffect(() => {
    const q = query.trim();
    if (q.length < 2) { setResults([]); setSearching(false); return undefined; }
    let cancelled = false;
    setSearching(true);
    const timer = setTimeout(async () => {
      try {
        const data = await searchLinkOptions(q);
        if (cancelled) return;
        setResults(wantProducts ? data.products || [] : data.vendors || []);
        setError("");
      } catch (err) {
        if (!cancelled) setError(err.message || "Search failed");
      } finally {
        if (!cancelled) setSearching(false);
      }
    }, 250);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [query, wantProducts]);

  const chosenName = wantProducts ? linkParams?.productName : linkParams?.vendorName;
  if (linkTarget && chosenName) {
    const kindLabel = LINK_KIND_LABEL[linkParams?.kind] || "shop";
    return (
      <div style={{
        display: "flex", alignItems: "center", gap: 12,
        border: `1px solid ${C.border}`, borderRadius: C.radiusSm, padding: "10px 12px", background: C.panel,
      }}>
        {linkParams?.image ? (
          <img src={linkParams.image} alt="" style={{ width: 44, height: 44, borderRadius: 6, objectFit: "cover", border: `1px solid ${C.border}`, flexShrink: 0 }} />
        ) : (
          <div style={{ width: 44, height: 44, borderRadius: 6, background: "#f0f2f5", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
            <FiImage size={16} style={{ color: C.textLight }} />
          </div>
        )}
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: 13, fontWeight: 700, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            <FiCheck size={12} style={{ color: C.teal, marginRight: 5 }} />{chosenName}
          </div>
          <div style={{ fontSize: 12, color: C.textLight }}>
            {wantProducts
              ? `${linkParams?.vendorName || "Unknown shop"} (${kindLabel})`
              : kindLabel}
            {linkParams?.enabled === false && <span style={{ color: C.orange, fontWeight: 600 }}> · currently disabled</span>}
          </div>
        </div>
        <button type="button" style={{ ...smallButton("transparent", C.blue), border: `1px solid ${C.border}` }} onClick={onClear}>
          Change
        </button>
      </div>
    );
  }

  return (
    <div>
      <div style={{ position: "relative" }}>
        <FiSearch size={13} style={{ position: "absolute", left: 11, top: 11, color: C.textLight }} />
        <input
          type="text"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder={wantProducts ? "Type a product name, e.g. K-9 Dog Food" : "Type a shop, restaurant or pharmacy name"}
          style={{ ...fieldStyle, paddingLeft: 30 }}
          autoComplete="off"
        />
        {searching && <Spinner animation="border" size="sm" style={{ position: "absolute", right: 11, top: 10, width: 14, height: 14 }} />}
      </div>
      {error && <InfoBox tone="red">{error}</InfoBox>}
      {query.trim().length >= 2 && !searching && results.length === 0 && !error && (
        <InfoBox tone="orange">Nothing matches "{query.trim()}" in this market. Check the spelling, or that the {wantProducts ? "product" : "shop"} has been added to Lusuku.</InfoBox>
      )}
      {results.length > 0 && (
        <div style={{ border: `1px solid ${C.border}`, borderRadius: C.radiusSm, marginTop: 6, overflow: "hidden", background: C.panel, maxHeight: 280, overflowY: "auto" }}>
          {results.map((r) => {
            const name = wantProducts ? r.productName : r.vendorName;
            const sub = wantProducts
              ? `${r.vendorName || "Unknown shop"} (${LINK_KIND_LABEL[r.kind] || r.kind})`
              : LINK_KIND_LABEL[r.kind] || r.kind;
            const flags = [];
            if (wantProducts && r.enabled === false) flags.push("disabled");
            if (wantProducts && r.inStock === false) flags.push("out of stock");
            if (r.vendorActive === false) flags.push("shop deactivated");
            return (
              <button
                key={`${r.kind}-${r.productId || r.vendorId}`}
                type="button"
                onClick={() => { onPick(r); setQuery(""); setResults([]); }}
                style={{
                  display: "flex", alignItems: "center", gap: 10, width: "100%", textAlign: "left",
                  padding: "8px 10px", background: "transparent", border: "none", borderBottom: `1px solid ${C.border}`, cursor: "pointer",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.background = C.bg)}
                onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
              >
                {r.image ? (
                  <img src={r.image} alt="" style={{ width: 36, height: 36, borderRadius: 6, objectFit: "cover", border: `1px solid ${C.border}`, flexShrink: 0 }} />
                ) : (
                  <div style={{ width: 36, height: 36, borderRadius: 6, background: "#f0f2f5", flexShrink: 0 }} />
                )}
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: C.text, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{name}</div>
                  <div style={{ fontSize: 11.5, color: C.textLight }}>
                    {sub}{flags.length > 0 && <span style={{ color: C.orange, fontWeight: 600 }}> · {flags.join(", ")}</span>}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
      <span style={hintStyle}>Start typing and pick from the list. Tapping the banner opens this {wantProducts ? "product" : "shop"} in the apps and on the website.</span>
    </div>
  );
};

// ── The form ──────────────────────────────────────────────────────────────────
const AdForm = ({ ad, onSubmit, onHide, showCropModal, setShowCropModal, siblings = [] }) => {
  const initialCountry = ad?.country || getActiveCountry() || DEFAULT_COUNTRY;
  const initialArtwork = splitAdArtwork(ad);

  const [formData, setFormData] = useState({
    title:        ad?.title        || "",
    description:  ad?.description  || "",
    imageUrl:     initialArtwork.imageUrl,
    desktopImageUrl: initialArtwork.desktopImageUrl,
    alt:          ad?.alt          || "",
    slot:         ad?.slot         || "hero_carousel",
    linkType:     ad?.linkType     || "none",
    linkTarget:   ad?.linkTarget   || ad?.categoryName || ad?.externalLink || "",
    linkParams:   ad?.linkParams   || {},
    buttonText:   ad?.buttonText   || "",
    country:      initialCountry,
    startsAt:     toMarketDateInput(ad?.startsAt, marketTimeZone(initialCountry)),
    endsAt:       toMarketDateInput(ad?.endsAt, marketTimeZone(initialCountry)),
    order:        ad?.order        ?? 0,
    isActive:     ad?.isActive !== undefined ? ad.isActive : true,
  });

  // Crop pipeline state — one artwork at a time.
  const [cropArtwork,    setCropArtwork]    = useState(null); // artwork meta being cropped
  const [imagePreview,   setImagePreview]   = useState(null);
  const [crop,           setCrop]           = useState({ unit: "%", x: 0, y: 0, width: 100, height: 100 });
  const [completedCrop,  setCompletedCrop]  = useState(null);
  const [sourceNote,     setSourceNote]     = useState(null); // { tone, text }
  const [sourceBlocked,  setSourceBlocked]  = useState(false);
  const [uploading,      setUploading]      = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [uploadError,    setUploadError]    = useState(null);
  const [isCropping,     setIsCropping]     = useState(false);
  const [formError,      setFormError]      = useState(null);
  const [saving,         setSaving]         = useState(false);

  const slotMeta = SLOT_META[formData.slot];
  const artworks = slotMeta?.artworks || [];
  const derivedDeviceType = deriveDeviceType(formData.slot, formData.imageUrl, formData.desktopImageUrl);
  const legacyLinkType = ["deal", "internal_screen"].includes(formData.linkType);
  const siteOrigin = MARKETS[formData.country]?.siteOrigin || "https://lusuku.ug";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData((prev) => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  };

  // Changing the link type wipes the target: a product id is meaningless as a
  // category name, and vice versa.
  const handleLinkTypeChange = (e) => {
    const linkType = e.target.value;
    setFormData((prev) => ({ ...prev, linkType, linkTarget: "", linkParams: {} }));
  };

  // Changing the slot changes every artwork shape, so saved artwork cannot
  // be carried across — it would fail the server gate anyway.
  const handleSlotChange = (e) => {
    const slot = e.target.value;
    setFormData((prev) => ({ ...prev, slot, imageUrl: "", desktopImageUrl: "" }));
  };

  const pickFile = (artwork, file) => {
    if (!file) return;
    if (file.size > MAX_FILE_BYTES) {
      setUploadError("That file is too large. Maximum size is 10 MB.");
      return;
    }
    setUploadError(null);
    setSourceNote(null);
    setSourceBlocked(false);
    const reader = new FileReader();
    reader.onload = () => {
      setImagePreview(reader.result);
      setCropArtwork(artwork);
      setShowCropModal(true);
    };
    reader.onerror = () => setUploadError("Could not read that image file.");
    reader.readAsDataURL(file);
  };

  // When the source loads: check it is big enough, then pre-fill the largest
  // possible window at the artwork's shape (the whole image when it already
  // has that shape, so a correctly made file needs no fiddling).
  const onImageLoad = useCallback((event) => {
    const el = event.currentTarget;
    const { width: dispW, height: dispH, naturalWidth, naturalHeight } = el;
    if (!dispW || !dispH || !cropArtwork) return;

    const { aspectRatio, canonWidthPx, canonHeightPx, minWidthPx } = cropArtwork;
    const natAspect = naturalWidth / naturalHeight;
    const minHeightPx = Math.round(minWidthPx / aspectRatio);
    const exactShape = Math.abs(natAspect - aspectRatio) / aspectRatio < 0.005;
    const smallerThanIdeal = naturalWidth < canonWidthPx || naturalHeight < canonHeightPx;
    const enlargeFactor = (canonWidthPx / naturalWidth).toFixed(1);
    const size = `${naturalWidth} by ${naturalHeight} pixels`;

    // Three honest tiers. The shape is what matters most (a wrong shape gets
    // cropped, and that is what loses wordmarks and badges); pixel size only
    // affects sharpness on big high-density screens, so a right-shaped file
    // that is merely smaller than ideal is told so calmly, not as an error.
    if (naturalWidth < minWidthPx || naturalHeight < minHeightPx) {
      setSourceBlocked(true);
      setSourceNote({
        tone: "red",
        text: `${size}: too small for ${cropArtwork.label.toLowerCase()}. It needs at least ${minWidthPx} by ${minHeightPx}, and ideally ${canonWidthPx} by ${canonHeightPx}. Ask for a larger export.`,
      });
    } else if (exactShape && smallerThanIdeal) {
      setSourceBlocked(false);
      setSourceNote({
        tone: "blue",
        text: `${size}: the right shape, so the whole image is selected. It is smaller than the ideal ${canonWidthPx} by ${canonHeightPx}, so it is saved enlarged ${enlargeFactor} times. That looks fine on laptop screens and may look slightly soft on large or high-resolution displays; a ${canonWidthPx} by ${canonHeightPx} export is sharper everywhere.`,
      });
    } else if (smallerThanIdeal) {
      setSourceBlocked(false);
      setSourceNote({
        tone: "orange",
        text: `${size}: not the ${cropArtwork.displayLabel} shape, so the box shows what will be kept (drag it to choose), and it is smaller than the ideal ${canonWidthPx} by ${canonHeightPx}, so the kept part is enlarged. A ${canonWidthPx} by ${canonHeightPx} export designed for this shape avoids both.`,
      });
    } else {
      setSourceBlocked(false);
      setSourceNote({
        tone: "blue",
        text: exactShape
          ? `${size}: the right shape and size. The whole image is selected.`
          : `${size}. Drag the box to choose which part of the image is kept; the shape is locked to ${cropArtwork.displayLabel}.`,
      });
    }
    const percentCrop = exactShape
      ? { unit: "%", x: 0, y: 0, width: 100, height: 100 }
      : centerCrop(makeAspectCrop({ unit: "%", width: 100 }, aspectRatio, dispW, dispH), dispW, dispH);

    setCrop(percentCrop);
    setCompletedCrop({
      unit:   "px",
      x:      (percentCrop.x      / 100) * dispW,
      y:      (percentCrop.y      / 100) * dispH,
      width:  (percentCrop.width  / 100) * dispW,
      height: (percentCrop.height / 100) * dispH,
    });
  }, [cropArtwork]);

  // Render the selection into a canvas at the artwork's canonical resolution
  // (1600×900 phone, 3159×498 desktop, ...), never at the cropper's CSS size,
  // so what reaches Cloudinary is exactly what the server gate expects.
  const renderCropToCanvas = useCallback((imageElement, cropRectPx, artwork) => {
    const scaleX = imageElement.naturalWidth  / imageElement.width;
    const scaleY = imageElement.naturalHeight / imageElement.height;
    const sx = cropRectPx.x * scaleX;
    const sy = cropRectPx.y * scaleY;
    const sw = cropRectPx.width * scaleX;
    const sh = cropRectPx.height * scaleY;

    const canvas  = document.createElement("canvas");
    canvas.width  = artwork.canonWidthPx;
    canvas.height = artwork.canonHeightPx;
    const ctx = canvas.getContext("2d");
    ctx.imageSmoothingEnabled = true;
    ctx.imageSmoothingQuality = "high";
    ctx.drawImage(imageElement, sx, sy, sw, sh, 0, 0, canvas.width, canvas.height);

    return new Promise((resolve, reject) =>
      canvas.toBlob((blob) => {
        if (!blob) return reject(new Error("The browser could not render the crop."));
        blob.name = `${artwork.key}-artwork.jpeg`;
        blob.__renderedWidthPx  = canvas.width;
        blob.__renderedHeightPx = canvas.height;
        resolve(blob);
      }, "image/jpeg", 0.92),
    );
  }, []);

  const handleCropSave = async () => {
    if (!imagePreview || !completedCrop?.width || !cropArtwork || sourceBlocked) return;
    setIsCropping(true);
    try {
      const imageElement = document.getElementById("adCropImage");
      const blob = await renderCropToCanvas(imageElement, completedCrop, cropArtwork);
      if (blob.__renderedWidthPx !== cropArtwork.canonWidthPx || blob.__renderedHeightPx !== cropArtwork.canonHeightPx) {
        throw new Error(`Cropped image is ${blob.__renderedWidthPx} by ${blob.__renderedHeightPx}, expected ${cropArtwork.canonWidthPx} by ${cropArtwork.canonHeightPx}.`);
      }
      setUploading(true);
      setUploadProgress(0);

      // A SIGNED upload, so no upload preset is involved. The dashboard's
      // unsigned preset (lusuku-clou) shrinks anything wider than 2000px,
      // which turned every 3159×498 desktop strip into 2000×315 and made the
      // server reject it after the fact. If the server cannot sign (local
      // dev), it hands back the preset and the size check below catches it.
      let intent = null;
      try {
        intent = await requestArtworkUploadIntent({
          slot: formData.slot,
          surface: cropArtwork.key,
          fileName: blob.name,
        });
      } catch (intentErr) {
        intent = null;
      }

      // Skip validation AND compression: the blob is already at canonical
      // resolution and 0.92 quality; the shared compressor would downscale
      // anything wider than 1600px (the 3159px desktop strip included).
      const result = await sharedUpload(blob, {
        skipValidation:  true,
        skipCompression: true,
        signed: intent?.mode === "signed" ? intent.cloudinary : null,
        onProgress: (pct) => setUploadProgress(pct),
      });

      // Verify what Cloudinary STORED, not what we sent: this is the exact
      // check the server runs on save, so a shrunk upload is caught here,
      // while the picture is still on screen, with a reason.
      if (result.width && result.height &&
          (result.width !== cropArtwork.canonWidthPx || result.height !== cropArtwork.canonHeightPx)) {
        throw new Error(
          `Cloudinary stored this image as ${result.width} by ${result.height}, not ${cropArtwork.canonWidthPx} by ${cropArtwork.canonHeightPx}. ` +
          (intent?.mode === "signed"
            ? "The upload was signed, so an account-level incoming transformation must be resizing it. Check Cloudinary, Settings, Upload."
            : "The upload went through the \"lusuku-clou\" preset, whose incoming size limit is 2000px wide. Either enable signed uploads on the server (CLOUDINARY_API_KEY and CLOUDINARY_API_SECRET) or raise that preset's limit to at least 3200px."),
        );
      }

      const field = cropArtwork.field;
      setFormData((prev) => ({ ...prev, [field]: result.secure_url }));
      setShowCropModal(false);
      setImagePreview(null);
      setCropArtwork(null);
    } catch (err) {
      setUploadError(`Crop and upload failed: ${err.message}`);
    } finally {
      setIsCropping(false);
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const cancelCrop = () => {
    setShowCropModal(false);
    setImagePreview(null);
    setCropArtwork(null);
  };

  const removeArtwork = (artwork) =>
    setFormData((prev) => ({ ...prev, [artwork.field]: "" }));

  const handlePickLink = (row) => {
    const target = row.linkType === "product" ? row.productId : row.vendorId;
    setFormData((prev) => ({
      ...prev,
      linkTarget: target,
      linkParams: {
        kind: row.kind,
        vendorId: row.vendorId,
        vendorName: row.vendorName,
        productName: row.productName || "",
        image: row.image || "",
        enabled: row.enabled,
        path: row.path,
      },
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setFormError(null);

    const hasPhone = !!formData.imageUrl;
    const hasDesktop = !!formData.desktopImageUrl;
    if (artworks.length > 1 ? !hasPhone && !hasDesktop : !hasPhone) {
      setFormError(artworks.length > 1
        ? "Add at least one artwork: phone, desktop web, or both."
        : "Add the banner artwork before saving.");
      return;
    }
    if ((formData.linkType === "product" || formData.linkType === "vendor") && !formData.linkTarget) {
      setFormError(formData.linkType === "product" ? "Pick the product the banner should open." : "Pick the shop the banner should open.");
      return;
    }
    if ((formData.linkType === "category" || formData.linkType === "external") && !formData.linkTarget.trim()) {
      setFormError(formData.linkType === "category" ? "Type the category name." : "Type the link or site path.");
      return;
    }

    const tz = marketTimeZone(formData.country);
    const payload = {
      title: formData.title,
      description: formData.description,
      alt: formData.alt || formData.title,
      imageUrl: formData.imageUrl,
      // Always present, even when empty: this key is how the server knows to
      // derive deviceType from the artwork instead of trusting a dropdown.
      desktopImageUrl: formData.desktopImageUrl,
      slot: formData.slot,
      linkType: formData.linkType,
      linkTarget: formData.linkTarget.trim(),
      linkParams: formData.linkType === "product" || formData.linkType === "vendor" ? formData.linkParams : {},
      // Mirror into the deprecated fields so older mobile builds keep working.
      categoryName: formData.linkType === "category" ? formData.linkTarget.trim() : "",
      externalLink: formData.linkType === "external" ? formData.linkTarget.trim() : "",
      buttonText: formData.linkType === "none" ? "" : formData.buttonText,
      country: formData.country,
      startsAt: startOfMarketDay(formData.startsAt, tz),
      endsAt: endOfMarketDay(formData.endsAt, tz),
      order: Number(formData.order) || 0,
      isActive: formData.isActive,
    };

    // The parent closes the modal only when the server says yes. A rejected
    // artwork or link comes back here, next to the Save button, with the
    // uploads still in place.
    setSaving(true);
    try {
      const result = await onSubmit(payload);
      if (result && result.ok === false) {
        setFormError(result.message || "The banner could not be saved.");
      }
    } finally {
      setSaving(false);
    }
  };

  const previewHref = formData.linkType === "external" && formData.linkTarget.trim()
    ? (formData.linkTarget.trim().startsWith("/") ? `${siteOrigin}${formData.linkTarget.trim()}` : formData.linkTarget.trim())
    : null;

  const otherRows = (siblings || [])
    .filter((row) => row.slot === formData.slot && row._id !== ad?._id)
    .sort((a, b) => (a.order ?? 0) - (b.order ?? 0));

  return (
    <>
      {/* ── Crop modal: rendered OUTSIDE the <form> to avoid invalid nesting ── */}
      <Modal show={showCropModal} onHide={cancelCrop} size="lg" centered>
        <Modal.Header closeButton style={{ borderBottom: `1px solid ${C.border}` }}>
          <Modal.Title>
            {cropArtwork ? `Crop ${cropArtwork.label.toLowerCase()}` : "Crop image"}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body style={{ background: "#f4f6fb", textAlign: "center", padding: 24 }}>
          {imagePreview && cropArtwork && (
            <ReactCrop
              crop={crop}
              onChange={(c) => setCrop(c)}
              onComplete={(c) => setCompletedCrop(c)}
              aspect={cropArtwork.aspectRatio}
              style={{ maxWidth: "100%" }}
            >
              <img src={imagePreview} id="adCropImage" alt="Crop preview" onLoad={onImageLoad} style={{ maxWidth: "100%" }} />
            </ReactCrop>
          )}
          {cropArtwork && (
            <div style={{ marginTop: 12, fontSize: 11, color: C.textLight }}>
              Shape locked to {cropArtwork.displayLabel}. Saved at {cropArtwork.canonWidthPx} by {cropArtwork.canonHeightPx} pixels for {cropArtwork.surfaces}.
            </div>
          )}
          {sourceNote && <div style={{ textAlign: "left" }}><InfoBox tone={sourceNote.tone}>{sourceNote.text}</InfoBox></div>}
        </Modal.Body>
        <Modal.Footer style={{ borderTop: `1px solid ${C.border}` }}>
          <button
            type="button"
            onClick={cancelCrop}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: C.radiusSm, padding: "8px 16px",
              fontSize: 13, fontWeight: 600, color: C.textMid, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={handleCropSave}
            disabled={isCropping || !completedCrop?.width || sourceBlocked}
            style={{
              background: isCropping || !completedCrop?.width || sourceBlocked ? "#c8d6ea" : C.blue,
              border: "none", borderRadius: C.radiusSm, padding: "8px 20px",
              fontSize: 13, fontWeight: 700, color: "#fff", cursor: sourceBlocked ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {isCropping
              ? <><Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} /> Uploading...</>
              : "Crop & Save"}
          </button>
        </Modal.Footer>
      </Modal>

      <form onSubmit={handleSubmit} style={{ display: "flex", flexDirection: "column", gap: 0 }}>

        {/* Title */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Title</label>
          <input
            type="text" name="title" required
            value={formData.title} onChange={handleChange}
            style={fieldStyle} placeholder="e.g. SWT K-9 dog food"
          />
          <span style={hintStyle}>For your own list and for screen readers. Customers do not see it as text.</span>
        </div>

        {/* Slot */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Where it appears</label>
          <AppSelect name="slot" value={formData.slot} onChange={handleSlotChange} style={{ ...fieldStyle, appearance: "none" }}>
            {/* Reserved slots have no renderer, so they are hidden from
                creation; a legacy row already on one keeps it selectable so
                editing does not silently move it. */}
            {Object.entries(SLOT_META)
              .filter(([key, meta]) => !meta.reserved || key === formData.slot)
              .map(([key, meta]) => (
                <option key={key} value={key}>
                  {meta.label}{meta.reserved ? " (reserved, not live)" : ""}
                </option>
              ))}
          </AppSelect>
          {slotMeta && <InfoBox tone={slotMeta.reserved ? "orange" : "blue"}>{slotMeta.renders}</InfoBox>}
        </div>

        {/* Artwork, one box per surface shape */}
        <div style={sectionStyle}>
          <label style={labelStyle}>
            <FiImage size={11} style={{ marginRight: 4 }} />
            Banner artwork
          </label>
          <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
            {artworks.map((artwork) => (
              <ArtworkBox
                key={artwork.key}
                artwork={artwork}
                url={formData[artwork.field]}
                onPick={(file) => pickFile(artwork, file)}
                onRemove={() => removeArtwork(artwork)}
                maxWidth={artwork.key === DESKTOP_ARTWORK_KEY ? "100%" : 340}
              />
            ))}
          </div>

          {/* Derived read-out: where this banner will actually run */}
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "center", gap: 6, marginTop: 14 }}>
            <span style={{ fontSize: 12, color: C.textMid, fontWeight: 600, marginRight: 4 }}>Shows on:</span>
            {(artworks.length > 1 ? SURFACE_CHIPS : SURFACE_CHIPS.filter((c) => c.needs === "phone" && c.key !== "mweb")).map((chip) => {
              const on = chip.needs === "desktop" ? !!formData.desktopImageUrl : !!formData.imageUrl;
              return (
                <span key={chip.key} style={{
                  fontSize: 11.5, padding: "3px 10px", borderRadius: 999, fontWeight: on ? 700 : 500,
                  border: `1px solid ${on ? C.teal : C.border}`, color: on ? C.teal : C.textLight,
                  background: on ? C.tealSoft : "transparent",
                }}>
                  {chip.label}
                </span>
              );
            })}
          </div>
          {artworks.length > 1 && (!formData.imageUrl || !formData.desktopImageUrl) && (
            <span style={hintStyle}>
              {!formData.imageUrl && !formData.desktopImageUrl
                ? "Upload phone artwork, desktop artwork, or both. The banner only runs where it has artwork."
                : !formData.imageUrl
                  ? "No phone artwork yet, so this banner will not show in the apps or on mobile web."
                  : "No desktop artwork yet, so this banner will not show on the desktop website."}
            </span>
          )}

          {uploadError && <InfoBox tone="red">{uploadError}</InfoBox>}
          {uploading && (
            <div style={{ marginTop: 10 }}>
              <div style={{ fontSize: 12, color: C.textMid, marginBottom: 5, fontWeight: 600 }}>
                Uploading... {uploadProgress}%
              </div>
              <ProgressBar now={uploadProgress} style={{ height: 6, borderRadius: 4 }} />
            </div>
          )}
        </div>

        {/* Live simulation on every surface the artwork reaches */}
        {(formData.imageUrl || formData.desktopImageUrl) && (
          <div style={sectionStyle}>
            <label style={labelStyle}>Preview</label>
            <AdSimulation
              imageUrl={formData.imageUrl}
              imageUrlWebp={formData.imageUrl === ad?.imageUrl ? ad?.imageUrlWebp || "" : ""}
              desktopImageUrl={formData.desktopImageUrl}
              slot={formData.slot}
              title={formData.title}
              description={formData.description}
              deviceType={derivedDeviceType}
              linkType={formData.linkType}
              buttonText={formData.buttonText}
            />
          </div>
        )}

        {/* Link */}
        <div style={sectionStyle}>
          <label style={labelStyle}>
            <FiLink size={11} style={{ marginRight: 4 }} />
            Where does a tap go?
          </label>
          <AppSelect name="linkType" value={formData.linkType} onChange={handleLinkTypeChange} style={{ ...fieldStyle, appearance: "none" }}>
            {LINK_CHOICES.map((c) => <option key={c.value} value={c.value}>{c.label}</option>)}
            {legacyLinkType && <option value={formData.linkType}>{formData.linkType === "deal" ? "Deal (legacy)" : "App screen (legacy)"}</option>}
          </AppSelect>
        </div>

        {(formData.linkType === "product" || formData.linkType === "vendor") && (
          <div style={sectionStyle}>
            <LinkPicker
              linkType={formData.linkType}
              linkTarget={formData.linkTarget}
              linkParams={formData.linkParams}
              onPick={handlePickLink}
              onClear={() => setFormData((prev) => ({ ...prev, linkTarget: "", linkParams: {} }))}
            />
          </div>
        )}

        {formData.linkType === "category" && (
          <div style={sectionStyle}>
            <label style={labelStyle}>Category name</label>
            <input
              type="text" name="linkTarget"
              value={formData.linkTarget} onChange={handleChange}
              style={fieldStyle} placeholder="e.g. Groceries, Laundry"
            />
            <span style={hintStyle}>Exactly as it is spelled in Categorization. Opens the shop filtered to that category.</span>
          </div>
        )}

        {formData.linkType === "external" && (
          <div style={sectionStyle}>
            <label style={labelStyle}>Link or site path</label>
            <input
              type="text" /* not type="url": site paths like /restaurants are valid */
              name="linkTarget"
              value={formData.linkTarget} onChange={handleChange}
              style={fieldStyle} placeholder="/restaurants   or   https://example.com/promo"
            />
            <span style={hintStyle}>
              A path starting with "/" opens that page of lusuku.ug inside the apps and the website. A full address opens in the browser.
              {previewHref && (
                <>
                  {" "}
                  <a href={previewHref} target="_blank" rel="noopener noreferrer" style={{ color: C.blue, fontWeight: 600 }}>
                    Open it to check <FiExternalLink size={10} />
                  </a>
                </>
              )}
            </span>
          </div>
        )}

        {legacyLinkType && (
          <div style={sectionStyle}>
            <label style={labelStyle}>{formData.linkType === "deal" ? "Deal slug" : "Screen name"}</label>
            <input type="text" name="linkTarget" value={formData.linkTarget} onChange={handleChange} style={fieldStyle} />
            <InfoBox tone="orange">This link type no longer has a working destination in the apps or on the website. Choose a product, shop, category or link instead.</InfoBox>
          </div>
        )}

        {formData.linkType !== "none" && (
          <div style={sectionStyle}>
            <label style={labelStyle}>Button text (website only, optional)</label>
            <input
              type="text" name="buttonText"
              value={formData.buttonText} onChange={handleChange}
              style={fieldStyle} placeholder="e.g. Shop now"
            />
            <span style={hintStyle}>The website draws a small button with this text over the banner. The whole banner is tappable everywhere, with or without it.</span>
          </div>
        )}

        {/* Market */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Market</label>
          <AppSelect name="country" value={formData.country} onChange={handleChange} style={{ ...fieldStyle, appearance: "none" }}>
            {SUPPORTED_COUNTRIES.map((code) => (
              <option key={code} value={code}>
                {MARKETS[code]?.name || code} ({code})
              </option>
            ))}
          </AppSelect>
          <span style={hintStyle}>Customers in {MARKETS[formData.country]?.name || formData.country} see this banner. Dates below are in that market's local time.</span>
        </div>

        {/* Scheduling */}
        <div style={{ ...sectionStyle, display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
          <div>
            <label style={labelStyle}>Starts on (optional)</label>
            <input type="date" name="startsAt" value={formData.startsAt} onChange={handleChange} style={fieldStyle} />
            <span style={hintStyle}>From 00:00 that day.</span>
          </div>
          <div>
            <label style={labelStyle}>Ends on (optional)</label>
            <input type="date" name="endsAt" value={formData.endsAt} onChange={handleChange} style={fieldStyle} />
            <span style={hintStyle}>Runs through the end of that day.</span>
          </div>
        </div>

        {/* Order */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Position in the carousel</label>
          <input
            type="number" name="order" min={0}
            value={formData.order} onChange={handleChange}
            style={{ ...fieldStyle, width: 120 }}
          />
          <span style={hintStyle}>Lower numbers come first. 0 is the first slide.</span>
          {otherRows.length > 0 && (
            <div style={{ marginTop: 8, border: `1px solid ${C.border}`, borderRadius: C.radiusSm, padding: "8px 12px", background: C.panel }}>
              <div style={{ fontSize: 11, fontWeight: 700, color: C.textLight, textTransform: "uppercase", letterSpacing: "0.06em", marginBottom: 4 }}>
                Also in this carousel
              </div>
              {otherRows.map((row) => (
                <div key={row._id} style={{ display: "flex", gap: 10, fontSize: 12, color: C.textMid, padding: "2px 0" }}>
                  <span style={{ fontVariantNumeric: "tabular-nums", color: C.textLight, width: 22 }}>{row.order ?? 0}</span>
                  <span style={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{row.title}</span>
                  <span style={{ color: C.textLight }}>{row.deviceType === "desktop" ? "desktop" : row.deviceType === "mobile" ? "phones" : "everywhere"}{row.isActive ? "" : " · paused"}</span>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Internal note + screen-reader text */}
        <div style={sectionStyle}>
          <label style={labelStyle}>Internal note (optional, not shown to customers)</label>
          <textarea
            name="description" rows={2}
            value={formData.description} onChange={handleChange}
            style={{ ...fieldStyle, resize: "vertical", minHeight: 48 }}
            placeholder="e.g. SWT campaign, runs until stock clears"
          />
        </div>
        <div style={sectionStyle}>
          <label style={labelStyle}>Image text for screen readers (optional)</label>
          <input
            type="text" name="alt"
            value={formData.alt} onChange={handleChange}
            style={fieldStyle} placeholder={formData.title || "Describe what the picture shows"}
          />
          <span style={hintStyle}>Read aloud to customers who use a screen reader. Defaults to the title.</span>
        </div>

        {/* Active toggle */}
        <div style={{ ...sectionStyle, display: "flex", alignItems: "center", justifyContent: "space-between", padding: "12px 14px", background: C.inputBg, borderRadius: C.radiusSm, border: `1px solid ${C.border}` }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Active</div>
            <div style={{ fontSize: 11, color: C.textLight }}>Paused banners are hidden from all customers.</div>
          </div>
          <button
            type="button"
            onClick={() => setFormData((prev) => ({ ...prev, isActive: !prev.isActive }))}
            style={{ background: "transparent", border: "none", cursor: "pointer", padding: 0, display: "flex", alignItems: "center" }}
          >
            {formData.isActive
              ? <FiToggleRight size={32} style={{ color: C.teal }} />
              : <FiToggleLeft  size={32} style={{ color: C.textLight }} />}
          </button>
        </div>

        {formError && <InfoBox tone="red">{formError}</InfoBox>}

        {/* Actions */}
        <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
          <button
            type="button" onClick={onHide}
            style={{
              background: "transparent", border: `1px solid ${C.border}`,
              borderRadius: C.radiusSm, padding: "9px 18px",
              fontSize: 13, fontWeight: 600, color: C.textMid, cursor: "pointer",
            }}
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={uploading || isCropping || saving}
            style={{
              background: uploading || isCropping || saving ? "#c8d6ea" : C.blue,
              border: "none", borderRadius: C.radiusSm, padding: "9px 24px",
              fontSize: 13, fontWeight: 700, color: "#fff",
              cursor: uploading || isCropping || saving ? "not-allowed" : "pointer",
              display: "inline-flex", alignItems: "center", gap: 8,
            }}
          >
            {uploading || isCropping || saving
              ? <><Spinner animation="border" size="sm" style={{ width: 13, height: 13 }} /> {saving ? "Saving..." : "Uploading..."}</>
              : ad ? "Save changes" : "Create banner"}
          </button>
        </div>
      </form>
    </>
  );
};

export default AdForm;
